import { Avatar, Button, Dialog, Flex, RadioCards, Text } from '@radix-ui/themes'
import { ArrowRightIcon, LogOutIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useSWRMutation from 'swr/mutation'
import { useState } from 'react'

import { User } from '../types/User'

import { stringToNamedColor } from '@/utils/stringToNamedColor'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/utils/api'

interface ProfileMutipleModalProps {
  onConfirm: (selectedId: string) => void
  onCancel: () => void
  user: User
}

export const UserProfileSelectModal = ({ user, onConfirm, onCancel }: ProfileMutipleModalProps) => {
  const [selectedId, setSelectedId] = useState(user.profiles[0]._id)
  const { t } = useTranslation()
  const { signIn } = useAuth()

  const { isMutating, trigger: updateSessionProfile } = useSWRMutation('login/profile', async (url: string) => {
    const profile = user.profiles.find(({ _id }) => _id === selectedId)

    if (!profile) {
      throw Error()
    }

    /*
    El token del login trae los roles del PRIMER perfil. La API lo vuelve a firmar con los
    del perfil elegido y hay que quedarse con ese: si no, la API rechaza con 403 lo que
    esta pantalla sí muestra (y deja pasar lo que no). Va con el token del login explícito
    porque signIn todavía no lo escribió en localStorage, que es de donde lo lee api.ts.
    Si la llamada falla se entra igual con el del login, como antes.
    */
    let token = user.token

    try {
      const { data } = await api.patch<{ token?: string }>(
        url,
        { profileId: profile._id },
        { headers: { Authorization: `Bearer ${user.token}` } }
      )

      if (data?.token) token = data.token
    } catch (error) {
      console.warn('No se pudo firmar el token del perfil elegido; se usa el del login', error)
    }

    signIn({ ...user, token }, profile)
  })

  const handleOnConfirm = async () => {
    await updateSessionProfile()
    onConfirm(selectedId)
  }

  return (
    <Dialog.Root open>
      <Dialog.Content maxWidth="30rem">
        <Dialog.Title>Organización</Dialog.Title>
        <RadioCards.Root
          defaultValue={user.profiles[0]._id}
          onValueChange={setSelectedId}
          value={selectedId}
          columns="1"
        >
          {user.profiles.map(({ _id, job, body }) => (
            <RadioCards.Item value={_id} key={_id}>
              <Avatar
                color={stringToNamedColor(body.organization.name)}
                fallback={body.organization.name[0]}
                radius="full"
              />
              <Flex direction="column" width="100%">
                <Text weight="bold">{body.organization.name}</Text>
                <Text>{t(job)}</Text>
              </Flex>
            </RadioCards.Item>
          ))}
        </RadioCards.Root>

        <Flex gap="3" mt="4" justify="between">
          <Dialog.Close>
            <Button onClick={onCancel} disabled={isMutating} variant="soft" color="gray">
              <Text>Cancelar</Text>
              <LogOutIcon />
            </Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button onClick={handleOnConfirm} disabled={!selectedId} loading={isMutating}>
              <Text>Continuar</Text>
              <ArrowRightIcon />
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
